/**
 * Service de gestion du cache processed pour éviter le retraitement GEDCOM
 * Version 1.1 : Compression dans Web Worker (non-bloquante)
 * Objectif: Passer de 5-8s de traitement à <200ms de restauration
 *
 * @file processedCacheService.js
 */

import { runInAction } from 'mobx';
import gedcomDataStore from '../gedcom/stores/gedcomDataStore.js';
import rootPersonStore from '../common/stores/rootPersonStore.js';
import { storeEvents, EVENTS } from '../common/stores/storeEvents.js';
import { logger } from './debug/logger.js';
import { getName, getSurname, getBirthYear, getDeathYear } from '../gedcom/formatters/individualFormatters.js';
import LZString from 'lz-string';

class ProcessedCacheService {
    constructor() {
        this.version = "1.1.0";
        this.initialized = false;
        this.compressionWorker = null;
        this.pendingRequests = new Map();
    }

    /**
     * Initialise le Web Worker pour la compression.
     * Pattern projet : import dynamique avec suffixe ?worker (asset/resource,
     * cf. webpack.config.js) qui exporte l'URL du bundle worker — le pattern
     * `new URL(..., import.meta.url)` n'est pas transformé par notre config
     * et jetait "Invalid URL" en production (fallback synchrone systématique).
     * @private
     */
    async _initCompressionWorker() {
        if (this.compressionWorker) return;

        try {
            const { default: CompressionWorkerURL } =
                await import('./workers/compression.worker.js?worker');
            this.compressionWorker = new Worker(CompressionWorkerURL, { type: 'module' });

            this.compressionWorker.onmessage = (event) => {
                const { type, requestId, result, stats, error, message, step } = event.data;
                const pending = this.pendingRequests.get(requestId);

                if (!pending) return;

                switch (type) {
                    case 'complete':
                        logger.cache.debug(`🗜️ Compression Worker terminée: ${stats.originalSize / 1024 / 1024 | 0} MB → ${stats.compressedSize / 1024 / 1024 | 0} MB (-${stats.compressionRatio}%) en ${stats.duration}ms`);
                        pending.resolve(result);
                        this.pendingRequests.delete(requestId);
                        break;
                    case 'progress':
                        logger.cache.debug(`🗜️ Worker: ${message}`);
                        break;
                    case 'error':
                        logger.cache.error(`❌ Worker error: ${error}`);
                        pending.reject(new Error(error));
                        this.pendingRequests.delete(requestId);
                        break;
                }
            };

            this.compressionWorker.onerror = (error) => {
                logger.cache.error('❌ Compression Worker error:', error);
            };

            logger.cache.debug('✅ Compression Worker initialisé');
        } catch (error) {
            logger.cache.warn('⚠️ Web Worker non supporté, fallback sur compression synchrone:', error);
            this.compressionWorker = null;
        }
    }

    /**
     * Compresse le cache en utilisant le Web Worker (non-bloquant)
     * @param {Object} cache - Cache à compresser
     * @returns {Promise<string>} Cache compressé
     */
    async compressCacheAsync(cache) {
        // Initialiser le worker si nécessaire
        await this._initCompressionWorker();

        // Sérialisation côté main thread : le cache contient des observables
        // MobX (Proxies) que postMessage ne peut pas cloner (DataCloneError).
        // JSON.stringify les traverse sans problème (~50 ms) ; la partie
        // lourde (compression LZ) reste dans le worker.
        const jsonString = JSON.stringify(cache);

        // Fallback synchrone si le worker n'est pas disponible
        if (!this.compressionWorker) {
            logger.cache.debug('🗜️ Compression synchrone (fallback)...');
            return LZString.compressToBase64(jsonString);
        }

        // Compression asynchrone via Worker
        return new Promise((resolve, reject) => {
            const requestId = `compress-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            this.pendingRequests.set(requestId, { resolve, reject });

            this.compressionWorker.postMessage({
                type: 'compress',
                data: jsonString,
                requestId
            });
        });
    }

    /**
     * Construit le cache processed SANS compression (rapide, non-bloquant)
     * @returns {Object} Cache processed non compressé
     */
    async buildProcessedCache() {
        logger.cache.time('BuildProcessedCache');
        logger.cache.debug('🏗️ Construction du cache processed (sans compression)...');

        try {
            const processedCache = {
                // Métadonnées de validation (avec checksum async)
                metadata: await this.buildMetadata(),

                // Données principales (optimisées Phase 6 Cloud)
                individualsCache: Array.from(gedcomDataStore.individualsCache.entries()),

                // 🏠 CRUCIAL: Exporter familiesCache pour conserver les événements de mariage
                familiesCache: Array.from(gedcomDataStore.familiesCache.entries()),

                // 📝 CRUCIAL: Exporter notesCache pour conserver les biographies et notes
                notesCache: Array.from(gedcomDataStore.notesCache.entries()),

                // 📚 CRUCIAL: Exporter sourcesCache pour conserver les sources
                sourcesCache: Array.from(gedcomDataStore.sourcesCache.entries()),

                // 🖼️ CRUCIAL: Exporter mediasCache pour conserver les médias
                mediasCache: Array.from(gedcomDataStore.mediasCache.entries()),

                // 🏛️ CRUCIAL: Exporter repositoriesCache pour conserver les dépôts (sources)
                repositoriesCache: Array.from(gedcomDataStore.repositoriesCache.entries()),

                // Données géographiques consolidées
                geographic: await this.exportGeographicData(),

                // Configuration par défaut
                config: this.exportConfig()
            };

            const cacheSize = this.calculateCacheSize(processedCache);
            logger.cache.debug(`📊 Cache processed construit: ${cacheSize.mb} MB (${cacheSize.kb} KB) - ${gedcomDataStore.individualsCache.size} individus`);
            logger.cache.timeEnd('BuildProcessedCache');

            return processedCache;

        } catch (error) {
            logger.cache.error('❌ Erreur construction cache processed:', error);
            throw error;
        }
    }

    /**
     * Génère un cache processed complet ET compressé pour upload R2
     * ⚠️ ATTENTION: La compression est effectuée dans un Web Worker pour ne pas bloquer l'UI
     * @returns {Promise<string>} Cache processed compressé avec LZ-String
     */
    async generateProcessedCache() {
        logger.cache.time('GenerateProcessedCache');

        try {
            // 1. Construire le cache (rapide)
            const processedCache = await this.buildProcessedCache();

            // 2. Compresser dans le Worker (non-bloquant)
            logger.cache.debug('🗜️ Compression LZ-String via Web Worker...');
            const compressedCache = await this.compressCacheAsync(processedCache);

            const compressedSize = new Blob([compressedCache]).size;
            logger.cache.debug(`💾 Prêt pour upload R2 - ${(compressedSize / 1024).toFixed(0)} KB compressés`);
            logger.cache.timeEnd('GenerateProcessedCache');

            return compressedCache;

        } catch (error) {
            logger.cache.error('❌ Erreur génération cache processed:', error);
            throw error;
        }
    }

    /**
     * Restaure l'état complet depuis un cache processed compressé
     * @param {string} compressedCache - Cache processed compressé avec LZ-String
     */
    async restoreFromProcessedCache(cacheData) {
        logger.cache.time('RestoreProcessedCache');
        logger.cache.debug('🚀 Restauration depuis cache processed (session personnelle)...');

        try {
            let processedCache;
            
            // Déterminer si les données sont déjà décompressées ou non
            if (typeof cacheData === 'string') {
                // Les données sont compressées, les décompresser
                logger.cache.time('LZStringDecompression');
                logger.cache.debug('🗜️ Décompression LZ-String en cours...');
                
                // Utiliser decompressFromBase64 pour correspondre à la méthode de compression
                const decompressedJSON = LZString.decompressFromBase64(cacheData);
                if (!decompressedJSON) {
                    throw new Error('Échec de la décompression LZ-String - cache corrompu');
                }
                
                processedCache = JSON.parse(decompressedJSON);
            } else if (typeof cacheData === 'object' && cacheData !== null) {
                // Les données sont déjà un objet décompressé
                logger.cache.debug('📦 Cache déjà décompressé, utilisation directe');
                processedCache = cacheData;
            } else {
                throw new Error('Format de cache invalide - doit être une string compressée ou un objet');
            }
            
            const decompressedSize = this.calculateCacheSize(processedCache);
            
            // Calculer la taille compressée seulement si on a reçu une string
            if (typeof cacheData === 'string') {
                const compressedSize = {
                    bytes: new Blob([cacheData]).size,
                    kb: Math.round(new Blob([cacheData]).size / 1024),
                    mb: (new Blob([cacheData]).size / 1024 / 1024).toFixed(2)
                };
                
                const compressionRatio = ((decompressedSize.bytes - compressedSize.bytes) / decompressedSize.bytes * 100).toFixed(1);
                logger.cache.debug(`🗜️ Décompression réussie: ${compressedSize.mb} MB → ${decompressedSize.mb} MB (+${compressionRatio}% décompressé)`);
                logger.cache.timeEnd('LZStringDecompression');
            }
            
            // Émettre événement restauration cache
            storeEvents.emit(EVENTS.GEDCOM.IMPORT_STARTED, { type: 'cache_restore', reason: 'cache_available' });
            
            // Validation de compatibilité
            if (!this.isCompatible(processedCache.metadata)) {
                throw new Error('Cache processed incompatible ou corrompu');
            }

            // Validation du checksum pour vérifier l'intégrité
            const checksumValid = await this.validateChecksum(processedCache, processedCache.metadata.checksum);
            if (!checksumValid) {
                throw new Error('Cache processed corrompu - échec validation checksum');
            }

            // IMPORTANT: Restaurer les données géographiques EN PREMIER (comme dans l'import GEDCOM)
            // Car tout le reste peut en dépendre pour l'enrichissement des données
            logger.cache.debug('🗺️ Restauration des données géographiques en premier');
            await this.restoreGeographicData(processedCache.geographic);
            
            runInAction(() => {
                // Restauration directe du cache individus (optimisé Phase 6 Cloud)
                const individualsMap = new Map(processedCache.individualsCache);

                // Phase 6 Cloud FIX: Décompresser toutes les dates essentielles et townKeys pour tous les individus
                const decompressionStats = this.decompressAllEssentialDates(individualsMap);
                logger.cache.debug(`🔧 Décompression: ${decompressionStats.birthDates} naissances, ${decompressionStats.deathDates} décès, ${decompressionStats.townKeys} clés villes, ${decompressionStats.years} années extraites`);

                gedcomDataStore.setIndividualsCache(individualsMap);

                logger.cache.debug(`✅ ${individualsMap.size} individus restaurés depuis cache`);

                // 🏠 CRUCIAL: Restaurer familiesCache pour récupérer les événements de mariage
                if (processedCache.familiesCache && Array.isArray(processedCache.familiesCache)) {
                    const familiesMap = new Map(processedCache.familiesCache);
                    gedcomDataStore.setFamiliesCache(familiesMap);
                    logger.cache.debug(`🏠 ${familiesMap.size} familles restaurées depuis cache (mariages, divorces, enfants)`);
                } else {
                    logger.cache.warn('⚠️ Aucune familiesCache trouvée dans le cache - événements de famille manquants');
                }

                // 📝 CRUCIAL: Restaurer notesCache pour récupérer les biographies
                if (processedCache.notesCache && Array.isArray(processedCache.notesCache)) {
                    const notesMap = new Map(processedCache.notesCache);
                    gedcomDataStore.setNotesCache(notesMap);
                    logger.cache.debug(`📝 ${notesMap.size} notes restaurées depuis cache (biographies, annotations)`);
                } else {
                    logger.cache.warn('⚠️ Aucune notesCache trouvée dans le cache - biographies manquantes');
                }

                // 📚 CRUCIAL: Restaurer sourcesCache pour récupérer les sources
                if (processedCache.sourcesCache && Array.isArray(processedCache.sourcesCache)) {
                    const sourcesMap = new Map(processedCache.sourcesCache);
                    gedcomDataStore.setSourcesCache(sourcesMap);
                    logger.cache.debug(`📚 ${sourcesMap.size} sources restaurées depuis cache`);
                } else {
                    logger.cache.debug('ℹ️ Aucune sourcesCache dans le cache (optionnel)');
                }

                // 🖼️ CRUCIAL: Restaurer mediasCache pour récupérer les médias
                if (processedCache.mediasCache && Array.isArray(processedCache.mediasCache)) {
                    const mediasMap = new Map(processedCache.mediasCache);
                    gedcomDataStore.setMediasCache(mediasMap);
                    logger.cache.debug(`🖼️ ${mediasMap.size} médias restaurés depuis cache`);
                } else {
                    logger.cache.debug('ℹ️ Aucune mediasCache dans le cache (optionnel)');
                }

                // 🏛️ CRUCIAL: Restaurer repositoriesCache pour récupérer les dépôts
                if (processedCache.repositoriesCache && Array.isArray(processedCache.repositoriesCache)) {
                    const repositoriesMap = new Map(processedCache.repositoriesCache);
                    gedcomDataStore.setRepositoriesCache(repositoriesMap);
                    logger.cache.debug(`🏛️ ${repositoriesMap.size} dépôts restaurés depuis cache`);
                } else {
                    logger.cache.debug('ℹ️ Aucune repositoriesCache dans le cache (optionnel)');
                }
            });
            
            // Restauration des indices de famille SUPPRIMÉE - Relations directes dans individualsCache
            // this.restoreFamilyIndices(processedCache.familyIndices); // ⚠️ PLUS NÉCESSAIRE
            
            // MAINTENANT on peut émettre l'événement BULK_ADDED car les données géo sont disponibles
            // Ceci déclenchera le traitement des événements dans familyTownsStore et surnamesTownsStore
            logger.cache.debug('📢 Émission de INDIVIDUALS.BULK_ADDED pour reconstruction des événements');
            logger.cache.debug('📊 ProcessedCache: townsData disponible avant BULK_ADDED: vérification en cours...');
            storeEvents.emit(EVENTS.INDIVIDUALS.BULK_ADDED, processedCache.individualsCache);
            
            // Restauration de la configuration
            this.restoreConfig(processedCache.config);

            // Émission des événements de fin comme après un traitement GEDCOM normal
            // Standardisation: Métadonnées de cache depuis service
            const cacheSize = this.calculateCacheSize(processedCache);
            storeEvents.emit(EVENTS.CACHE.BUILT, {
                source: 'cache_restore',
                individualsCount: gedcomDataStore.totalIndividuals,
                cacheSize: cacheSize.bytes,
                cacheSizeMB: cacheSize.mb,
                isDirectImport: true,
                isRestoringFromCache: true,
                timestamp: new Date().toISOString(),
                cacheVersion: processedCache.metadata?.version || '1.0'
            });
            
            // IMPORTANT: Peupler TomSelect avec les individus avant de sélectionner la racine
            this.populateTomSelectFromCache();
            
            // IMPORTANT: Sélectionner et définir la personne racine initiale
            // Utilise la même logique que gedcomFileHandler.js
            this.selectAndSetInitialRoot();
            
            // IMPORTANT: Émettre PROCESS.COMPLETE pour activer les autres onglets
            // Standardisation: Métadonnées de processus restauration cache
            storeEvents.emit(EVENTS.PROCESS.COMPLETE, {
                step: 'cache_restore_completed',
                message: 'Restauration cache terminée avec succès',
                duration: Date.now() - (processedCache.metadata?.processStartTime || Date.now()),
                timestamp: new Date().toISOString(),
                processId: `cache_restore_${Date.now()}`,
                individualsCount: gedcomDataStore.totalIndividuals
            });
            
            // IMPORTANT: Assurer le bon centrage de la carte après restauration
            await this.ensureMapCenteringAfterRestore();
            
            // IMPORTANT: Déclencher le calcul des statistiques après restauration cache
            await this.initializeStatisticsAfterRestore();
            
            const finalCacheSize = this.calculateCacheSize(processedCache);
            logger.cache.debug(`✅ Session restaurée en <200ms depuis cache (${finalCacheSize.mb} MB)`);
            logger.cache.debug(`🎯 Évitement du retraitement GEDCOM (gain 5-8s)`);
            logger.cache.timeEnd('RestoreProcessedCache');
            
            // Marquer la fin de la restauration cache
            gedcomDataStore.setRestoringFromCache(false);

        } catch (error) {
            logger.cache.error('❌ Erreur restauration cache processed:', error);
            // Marquer la fin de la restauration cache même en cas d'erreur
            gedcomDataStore.setRestoringFromCache(false);
            throw error;
        }
    }

    /**
     * Sélectionne et définit la personne racine initiale
     * Reprend la même logique que gedcomFileHandler pour la cohérence
     */
    selectAndSetInitialRoot() {
        logger.cache.debug('🎯 Sélection de la personne racine initiale...');
        
        // Phase 11: Utiliser entries() pour avoir accès aux IDs
        const individualsEntries = Array.from(gedcomDataStore.individualsCache.entries());
        if (!individualsEntries || individualsEntries.length === 0) {
            logger.cache.warn('⚠️ Aucun individu trouvé pour définir la racine');
            return;
        }

        // Chercher la personne la plus jeune ou prendre la première
        let rootId;
        
        // Logique simplifiée: chercher la personne avec la date de naissance la plus récente
        // Phase 11: Ajouter temporairement 'i' pour findYoungestPerson
        const individualsWithId = individualsEntries.map(([id, data]) => ({ ...data, i: id }));
        const youngestPerson = this.findYoungestPerson(individualsWithId);
        rootId = youngestPerson ? youngestPerson.i : individualsEntries[0]?.[0];

        // Définir la personne racine
        if (rootId) {
            const rootPerson = gedcomDataStore.individualsCache.get(rootId);
            if (rootPerson) {
                logger.cache.debug(`✅ Personne racine sélectionnée: ${getName(rootPerson)} ${getSurname(rootPerson)}`);
                
                rootPersonStore.setRoot(rootId, { skipDraw: false });
                rootPersonStore.setRootPersonName({
                    name: getName(rootPerson),
                    surname: getSurname(rootPerson),
                });
                rootPersonStore.setTomSelectValue(rootId);
            }
        }
    }

    /**
     * Peuple TomSelect avec les individus depuis le cache
     */
    populateTomSelectFromCache() {
        logger.cache.debug('📋 Population de TomSelect depuis le cache...');
        
        const tomSelect = rootPersonStore.tomSelect;
        if (!tomSelect) {
            logger.cache.warn('⚠️ TomSelect non initialisé');
            return;
        }

        // Vider les options existantes
        tomSelect.clearOptions();

        // Phase 11: Utiliser entries() pour avoir accès aux IDs
        const individualsEntries = Array.from(gedcomDataStore.individualsCache.entries());
        let addedCount = 0;

        individualsEntries.forEach(([id, individual]) => {
            if (!individual) return;

            // Extraire birthYear/deathYear en utilisant les fonctions qui gèrent la décompression
            const birthYear = getBirthYear(individual) || '';
            const deathYear = getDeathYear(individual) || '';
            
            // Créer une version courte du nom pour l'affichage dans la dropdown
            const shortName = `${getSurname(individual) || ''} ${(getName(individual) || '').split(' ')[0]} (${birthYear || '?'}${deathYear ? '-'+deathYear : ''})`;
            
            // Créer une version complète pour le tooltip
            const fullName = `${getSurname(individual) || ''} ${getName(individual) || ''} ${id} ${birthYear || "?"}-${deathYear || ""}`;
            
            tomSelect.addOption({
                value: id, // Phase 11: Utiliser l'ID de la clé Map
                text: shortName.trim(),
                title: fullName.trim()
            });
            
            addedCount++;
        });

        logger.cache.debug(`✅ TomSelect peuplé avec ${addedCount} individus`);
    }

    /**
     * Assure le bon centrage de la carte après restauration du cache
     * Réinitialise les bounds calculés dans familyTownsStore
     */
    async ensureMapCenteringAfterRestore() {
        try {
            // Import dynamique du familyTownsStore
            const { default: familyTownsStore } = await import('../tabs/familyMap/stores/familyTownsStore.js');
            
            if (familyTownsStore && typeof familyTownsStore.ensureMapCenteringAfterRestore === 'function') {
                familyTownsStore.ensureMapCenteringAfterRestore();
                logger.cache.debug('🗺️ Centrage carte réinitialisé après cache');
            } else {
                logger.cache.debug('🗺️ FamilyTownsStore non disponible pour centrage');
            }
        } catch (error) {
            logger.cache.warn('⚠️ Erreur lors de la réinitialisation du centrage:', error);
        }
    }

    /**
     * Initialise les statistiques après restauration du cache
     * Équivalent à l'appel processFamilyData() dans l'import direct GEDCOM
     */
    async initializeStatisticsAfterRestore() {
        try {
            logger.cache.debug('📊 Initialisation des statistiques après restauration cache...');
            
            // Import dynamique du statisticsService
            const { statisticsService } = await import('../tabs/statistics/services/statisticsService.js');
            
            if (!statisticsService) {
                logger.cache.warn('⚠️ StatisticsService non disponible');
                return;
            }
            
            // Initialiser le service si nécessaire
            if (!statisticsService.worker) {
                logger.cache.debug('📊 Initialisation du worker de statistiques...');
                await statisticsService.initialize();
            }
            
            // Appeler processFamilyData() comme dans l'import direct GEDCOM
            logger.cache.debug('📊 Traitement des données familiales pour les statistiques...');
            await statisticsService.processFamilyData();
            
            logger.cache.debug('✅ Statistiques initialisées avec succès après restauration cache');
        } catch (error) {
            logger.cache.warn('⚠️ Erreur lors de l\'initialisation des statistiques:', error);
            // Ne pas faire échouer la restauration cache pour les statistiques
        }
    }

    /**
     * Trouve la personne la plus jeune dans la liste
     * @param {Array} individuals - Liste des individus
     * @returns {Object|null} - L'individu le plus jeune ou null
     */
    findYoungestPerson(individuals) {
        const individualsWithBirthDates = individuals
            .filter(individual => individual)
            .map((individual) => {
                // Obtenir birthDate depuis l'individu ou décompresser depuis les événements
                let birthDate = individual.birthDate;

                // Si pas de birthDate au niveau racine, chercher dans les événements compressés
                if (!birthDate && individual.e && individual.e.length > 0) {
                    const birthEvent = individual.e.find(e => e.t === 'fb' || e.type === 'birth');
                    if (birthEvent) {
                        birthDate = this.decompressDateFromEvent(birthEvent);
                    }
                }

                // Si toujours pas de date, exclure cet individu
                if (!birthDate) return null;
                let date;

                try {
                    if (birthDate.includes("/")) {
                        const parts = birthDate.split("/");
                        // Format DD/MM/YYYY ou D/M/YYYY
                        const day = parts[0];
                        const month = parts[1];
                        const year = parts[2];

                        // Ensure we have valid numbers for the date
                        if (year && !isNaN(year)) {
                            date = new Date(parseInt(year), (month ? parseInt(month) - 1 : 0), day ? parseInt(day) : 1);
                        } else {
                            return null;
                        }
                    } else if (birthDate && !isNaN(birthDate)) {
                        // If birthDate is just a year
                        date = new Date(parseInt(birthDate), 0, 1);
                    } else {
                        return null;
                    }

                    // Validate that we got a valid date
                    if (isNaN(date.getTime())) {
                        return null;
                    }

                    return {
                        individual,
                        birthTimestamp: date.getTime()
                    };
                } catch (error) {
                    logger.cache.warn(`Error processing birth date for individual:`, error);
                    return null;
                }
            })
            .filter(item => item && !isNaN(item.birthTimestamp));

        if (individualsWithBirthDates.length === 0) {
            return null;
        }

        // Trier par date de naissance (plus récent en premier = le plus jeune)
        individualsWithBirthDates.sort((a, b) => b.birthTimestamp - a.birthTimestamp);

        return individualsWithBirthDates[0]?.individual;
    }

    /**
     * Décompresse les dates essentielles (birthDate, deathDate) et townKeys depuis les événements compressés
     * Nécessaire car les processed.json stockent les dates dans les événements compressés
     * @param {Map} individualsMap - Map des individus à traiter
     * @returns {Object} - Statistiques de décompression
     */
    decompressAllEssentialDates(individualsMap) {
        const stats = { birthDates: 0, deathDates: 0, years: 0, townKeys: 0, total: 0 };
        
        individualsMap.forEach((individual, id) => {
            if (!individual.e || individual.e.length === 0) return;
            
            stats.total++;
            
            // Décompresser birthDate et birthTownKey
            if (!individual.birthDate || !individual.birthTownKey) {
                const birthEvent = individual.e.find(e => e.t === 'fb' || e.type === 'birth');
                if (birthEvent) {
                    // Décompresser la date
                    if (!individual.birthDate) {
                        const decompressedDate = this.decompressDateFromEvent(birthEvent);
                        if (decompressedDate) {
                            individual.birthDate = decompressedDate;
                            individual.birthYear = this.extractYearFromDate(decompressedDate);
                            stats.birthDates++;
                            stats.years++;
                        }
                    }
                    
                    // Décompresser le townKey
                    if (!individual.birthTownKey) {
                        const townKey = birthEvent.l || birthEvent.townKey;
                        if (townKey) {
                            individual.birthTownKey = townKey;
                            stats.townKeys++;
                        }
                    }
                }
            }
            
            // Décompresser deathDate et deathTownKey
            if (!individual.deathDate || !individual.deathTownKey) {
                const deathEvent = individual.e.find(e => e.t === 'fd' || e.type === 'death');
                if (deathEvent) {
                    // Décompresser la date
                    if (!individual.deathDate) {
                        const decompressedDate = this.decompressDateFromEvent(deathEvent);
                        if (decompressedDate) {
                            individual.deathDate = decompressedDate;
                            individual.deathYear = this.extractYearFromDate(decompressedDate);
                            stats.deathDates++;
                            stats.years++;
                        }
                    }
                    
                    // Décompresser le townKey
                    if (!individual.deathTownKey) {
                        const townKey = deathEvent.l || deathEvent.townKey;
                        if (townKey) {
                            individual.deathTownKey = townKey;
                            stats.townKeys++;
                        }
                    }
                }
            }
        });
        
        return stats;
    }
    
    /**
     * Décompresse une date depuis un événement compressé
     * @param {Object} event - Événement compressé ou non
     * @returns {string|null} - Date décompressée au format DD/MM/YYYY
     */
    decompressDateFromEvent(event) {
        if (!event) return null;
        
        // Si l'événement a déjà une date au format string, l'utiliser
        if (event.date && typeof event.date === 'string') {
            return event.date;
        }
        
        // Si l'événement a une date compressée (format numérique)
        if (event.d && typeof event.d === 'number') {
            return this.decompressNumericDate(event.d);
        }
        
        return null;
    }
    
    /**
     * Décompresse une date numérique YYYYMMDD vers DD/MM/YYYY
     * @param {number} compactDate - Date au format 19290720
     * @returns {string|null} - Date au format "20/07/1929"
     */
    decompressNumericDate(compactDate) {
        if (!compactDate) return null;
        
        try {
            const dateStr = compactDate.toString();
            if (dateStr.length !== 8) return null;
            
            const year = dateStr.slice(0, 4);
            const month = dateStr.slice(4, 6);
            const day = dateStr.slice(6, 8);
            
            // Si c'est une année seule (01/01), retourner juste l'année
            if (month === '01' && day === '01') {
                return year;
            }
            
            return `${parseInt(day)}/${parseInt(month)}/${year}`;
        } catch (error) {
            logger.cache.warn('Erreur décompression date:', compactDate, error);
            return null;
        }
    }
    
    /**
     * Extrait l'année d'une date au format DD/MM/YYYY ou YYYY
     * @param {string} date - Date au format "20/07/1929" ou "1929"
     * @returns {string} - Année au format "1929"
     */
    extractYearFromDate(date) {
        if (!date) return '';
        
        if (date.includes('/')) {
            const parts = date.split('/');
            return parts[2] || '';
        }
        
        return date;
    }

    /**
     * Construit les métadonnées de validation du cache avec checksum
     */
    async buildMetadata() {
        // Construire le contenu à hacher (sans checksum pour éviter la circularité)
        const cacheContent = {
            individualsCache: Array.from(gedcomDataStore.individualsCache.entries()),
            familiesCache: Array.from(gedcomDataStore.familiesCache.entries()),
            notesCache: Array.from(gedcomDataStore.notesCache.entries()),
            sourcesCache: Array.from(gedcomDataStore.sourcesCache.entries()),
            mediasCache: Array.from(gedcomDataStore.mediasCache.entries()),
            repositoriesCache: Array.from(gedcomDataStore.repositoriesCache.entries()),
            geographic: await this.exportGeographicData()
        };

        // Générer checksum SHA-256 pour validation d'intégrité
        const checksum = await this.generateChecksum(cacheContent);

        return {
            version: this.version,
            generatedAt: Date.now(),
            individualsCount: gedcomDataStore.individualsCache.size,
            familiesCount: gedcomDataStore.familiesCache.size,
            notesCount: gedcomDataStore.notesCache.size,
            sourcesCount: gedcomDataStore.sourcesCache.size,
            mediasCount: gedcomDataStore.mediasCache.size,
            repositoriesCount: gedcomDataStore.repositoriesCache.size,
            cacheType: 'personal_session', // Marqueur v1.0
            userAgent: navigator.userAgent,
            geneafanVersion: window.__GENEAFAN_VERSION__ || 'dev',
            checksum: checksum // ⬅️ NOUVEAU: Checksum SHA-256 pour validation
        };
    }

    /**
     * Génère un checksum SHA-256 pour validation d'intégrité du cache
     * @param {Object} data - Données à hacher
     * @returns {Promise<string>} - Checksum hexadécimal
     */
    async generateChecksum(data) {
        try {
            // Sérialiser les données de manière déterministe
            const jsonString = JSON.stringify(data);
            const encoder = new TextEncoder();
            const dataBuffer = encoder.encode(jsonString);

            // Générer hash SHA-256
            const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);

            // Convertir en hexadécimal
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const checksum = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

            logger.cache.debug(`🔐 Checksum généré: ${checksum.substring(0, 16)}...`);
            return checksum;
        } catch (error) {
            logger.cache.warn('⚠️ Erreur génération checksum:', error);
            return null; // Non-bloquant si échec
        }
    }

    /**
     * Valide le checksum d'un cache reçu
     * @param {Object} processedCache - Cache à valider
     * @param {string} expectedChecksum - Checksum attendu depuis les métadonnées
     * @returns {Promise<boolean>} - true si valide, false sinon
     */
    async validateChecksum(processedCache, expectedChecksum) {
        if (!expectedChecksum) {
            logger.cache.debug('⚠️ Pas de checksum dans les métadonnées - validation ignorée');
            return true; // Rétrocompatibilité avec anciens caches
        }

        try {
            // Recalculer le checksum sur les mêmes données
            const cacheContent = {
                individualsCache: processedCache.individualsCache,
                familiesCache: processedCache.familiesCache || [],
                notesCache: processedCache.notesCache || [],
                sourcesCache: processedCache.sourcesCache || [],
                mediasCache: processedCache.mediasCache || [],
                repositoriesCache: processedCache.repositoriesCache || [],
                geographic: processedCache.geographic
            };

            const calculatedChecksum = await this.generateChecksum(cacheContent);

            if (calculatedChecksum === expectedChecksum) {
                logger.cache.debug('✅ Checksum valide - intégrité confirmée');
                return true;
            } else {
                logger.cache.error('❌ Checksum invalide - cache corrompu !');
                logger.cache.debug(`  Attendu: ${expectedChecksum.substring(0, 16)}...`);
                logger.cache.debug(`  Calculé: ${calculatedChecksum.substring(0, 16)}...`);
                return false;
            }
        } catch (error) {
            logger.cache.error('❌ Erreur validation checksum:', error);
            return false;
        }
    }

    /**
     * Exporte les indices de famille pour restauration rapide des lookups
     */
    exportFamilyIndices() {
        try {
            const indices = gedcomDataStore.indices;
            if (!indices) {
                logger.cache.warn('⚠️ Indices famille non disponibles');
                return null;
            }

            return {
                byChild: Array.from(indices.byChild.entries()),
                byParent: Array.from(indices.byParent.entries()),
                byCouple: Array.from(indices.byCouple.entries())
            };
        } catch (error) {
            logger.cache.warn('⚠️ Impossible d\'exporter les indices de famille:', error);
            return null;
        }
    }

    /**
     * Exporte les données géographiques consolidées (villes normalisées + coordonnées)
     */
    async exportGeographicData() {
        try {
            // Import dynamique du store géographique principal
            const { default: familyTownsStore } = await import('../tabs/familyMap/stores/familyTownsStore.js');
            
            if (!familyTownsStore || !familyTownsStore.townsData) {
                logger.cache.warn('⚠️ Données géographiques non disponibles');
                return null;
            }

            // Calculer la liste des patronymes pour éviter de recalculer à la restauration
            const patronymesList = this.calculatePatronymesList(familyTownsStore.townsData);

            return {
                townsData: Array.from(familyTownsStore.townsData.entries()),
                eventsData: familyTownsStore.eventsData ? Array.from(familyTownsStore.eventsData.entries()) : [],
                calculatedBounds: familyTownsStore.calculatedBounds || null,
                patronymesList: patronymesList
            };
        } catch (error) {
            logger.cache.warn('⚠️ Impossible d\'exporter les données géographiques:', error);
            return null;
        }
    }

    /**
     * Calcule la liste des patronymes triée par fréquence
     * @param {Map} townsData - Map des données de villes
     * @returns {Array} Liste triée des patronymes avec leur fréquence
     */
    calculatePatronymesList(townsData) {
        logger.cache.debug('📊 Calcul de la liste des patronymes pour le cache...');
        const surnamesCount = new Map();

        // Compter les occurrences de chaque patronyme dans les événements de naissance
        townsData.forEach((townData, townName) => {
            if (townData.events && townData.events.birth) {
                townData.events.birth.forEach(event => {
                    const surname = event.personDetails?.surname;
                    if (surname && surname !== '?' && surname !== ',') {
                        surnamesCount.set(surname, (surnamesCount.get(surname) || 0) + 1);
                    }
                });
            }
        });

        // Trier les patronymes par fréquence décroissante
        const sortedSurnames = [...surnamesCount.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([surname, count]) => ({ surname, count }));

        logger.cache.debug(`✅ ${sortedSurnames.length} patronymes uniques trouvés`);
        return sortedSurnames;
    }

    /**
     * Exporte la configuration essentielle
     */
    exportConfig() {
        try {
            return {
                availableGenerations: this.calculateMaxGenerations(),
                timestamp: Date.now()
            };
        } catch (error) {
            logger.cache.warn('⚠️ Impossible d\'exporter la configuration:', error);
            return { availableGenerations: 8 };
        }
    }

    /**
     * Calcule le nombre maximum de générations disponibles
     */
    calculateMaxGenerations() {
        try {
            if (gedcomDataStore.calculateMaxGenerations) {
                return gedcomDataStore.calculateMaxGenerations(gedcomDataStore.individualsCache, []);
            }
            return 8; // Valeur par défaut
        } catch (error) {
            return 8;
        }
    }

    /**
     * Restaure les indices de famille pour performance
     */
    restoreFamilyIndices(familyIndices) {
        if (!familyIndices || !gedcomDataStore.indices) {
            logger.cache.warn('⚠️ Indices famille non restaurables');
            return;
        }

        try {
            gedcomDataStore.indices.byChild = new Map(familyIndices.byChild || []);
            gedcomDataStore.indices.byParent = new Map(familyIndices.byParent || []);
            gedcomDataStore.indices.byCouple = new Map(familyIndices.byCouple || []);
            
            logger.cache.debug('✅ Indices famille restaurés - lookups optimisés');
        } catch (error) {
            logger.cache.warn('⚠️ Erreur restauration indices famille:', error);
        }
    }

    /**
     * Restaure les données géographiques (villes + coordonnées)
     */
    async restoreGeographicData(geographic) {
        if (!geographic) {
            logger.cache.warn('⚠️ Pas de données géographiques à restaurer');
            return;
        }

        try {
            const { default: familyTownsStore } = await import('../tabs/familyMap/stores/familyTownsStore.js');
            
            if (!familyTownsStore) {
                logger.cache.warn('⚠️ familyTownsStore non disponible');
                return;
            }

            // Restauration des coordonnées des villes uniquement
            // Les événements seront reconstruits par BULK_ADDED
            familyTownsStore.townsData = new Map(geographic.townsData || []);
            
            // Ne pas restaurer eventsData ici car ils seront reconstruits
            // lors du traitement BULK_ADDED avec les bonnes références
            // if (geographic.eventsData && geographic.eventsData.length > 0) {
            //     familyTownsStore.eventsData = new Map(geographic.eventsData);
            // }
            
            if (geographic.calculatedBounds) {
                familyTownsStore.calculatedBounds = geographic.calculatedBounds;
            }

            logger.cache.debug(`✅ ${geographic.townsData?.length || 0} villes restaurées avec coordonnées`);
            
            // Restaurer directement la liste des patronymes si disponible
            if (geographic.patronymesList && geographic.patronymesList.length > 0) {
                this.restorePatronymesList(geographic.patronymesList);
                logger.cache.debug(`✅ ${geographic.patronymesList.length} patronymes restaurés depuis le cache`);
            }
        } catch (error) {
            logger.cache.warn('⚠️ Erreur restauration données géographiques:', error);
        }
    }

    /**
     * Restaure directement la liste des patronymes dans la dropdown
     * @param {Array} patronymesList - Liste triée des patronymes avec leur fréquence
     */
    restorePatronymesList(patronymesList) {
        logger.cache.debug('📋 Restauration directe de la liste des patronymes...');
        
        // Attendre que le DOM soit prêt
        const restoreToDOM = () => {
            const select = document.getElementById('surnameFilter');
            if (!select) {
                logger.cache.debug('⏳ Dropdown patronymes non encore disponible, nouvelle tentative dans 500ms');
                setTimeout(restoreToDOM, 500);
                return;
            }

            // Remplir la dropdown avec les patronymes du cache
            select.innerHTML = `
                <option value="">Sélectionner un patronyme...</option>
                ${patronymesList.map(({ surname, count }) =>
                    `<option value="${surname}">${surname.toUpperCase()} (${count})</option>`
                ).join('')}
            `;

            logger.cache.debug(`✅ Dropdown patronymes remplie avec ${patronymesList.length} entrées`);

            // Ne pas sélectionner automatiquement de patronyme pour éviter l'application 
            // automatique des filtres timeline. L'utilisateur choisira manuellement.
            logger.cache.debug(`🚫 Sélection automatique du patronyme désactivée pour laisser le choix à l'utilisateur`);
        };

        // Lancer la restauration
        restoreToDOM();
    }

    /**
     * Restaure la configuration de base
     */
    restoreConfig(config) {
        if (!config) return;

        try {
            // Configuration minimale pour v1.0 (sessions personnelles)
            logger.cache.debug(`✅ Configuration restaurée (${config.availableGenerations} générations max)`);
        } catch (error) {
            logger.cache.warn('⚠️ Erreur restauration configuration:', error);
        }
    }

    /**
     * Vérifie la compatibilité du cache processed
     */
    isCompatible(metadata) {
        if (!metadata) {
            logger.cache.warn('⚠️ Métadonnées manquantes dans le cache');
            return false;
        }

        // Version compatible 1.x.x
        if (!metadata.version || !metadata.version.startsWith('1.')) {
            logger.cache.warn('⚠️ Version incompatible:', metadata.version);
            return false;
        }

        // Cache type personnel
        if (metadata.cacheType !== 'personal_session') {
            logger.cache.warn('⚠️ Type de cache non supporté:', metadata.cacheType);
            return false;
        }

        // NB: no age-based expiry. The processed cache is now the durable
        // canonical artifact (the ".geneatree vivant" stored in R2 via
        // files-service), not a transient local session cache. A user who
        // explicitly opens a stored file must always be able to restore it,
        // however old it is — there is no raw .ged fallback server-side.
        // Integrity is guarded by the version / cacheType / count / checksum
        // checks, not by wall-clock age.

        // Validation du nombre d'individus
        if (!metadata.individualsCount || metadata.individualsCount < 1) {
            logger.cache.warn('⚠️ Cache sans individus valide');
            return false;
        }

        const cacheDetails = [
            `${metadata.individualsCount} individus`,
            metadata.familiesCount ? `${metadata.familiesCount} familles` : null,
            metadata.notesCount ? `${metadata.notesCount} notes` : null,
            metadata.sourcesCount ? `${metadata.sourcesCount} sources` : null,
            metadata.mediasCount ? `${metadata.mediasCount} médias` : null,
            metadata.repositoriesCount ? `${metadata.repositoriesCount} dépôts` : null
        ].filter(Boolean).join(', ');

        logger.cache.debug(`✅ Cache compatible: ${cacheDetails}, généré le ${new Date(metadata.generatedAt).toLocaleString()}`);
        return true;
    }

    /**
     * Calcule la taille du cache processed
     */
    calculateCacheSize(cache) {
        try {
            const cacheJSON = JSON.stringify(cache);
            const sizeBytes = new Blob([cacheJSON]).size;
            return {
                bytes: sizeBytes,
                kb: Math.round(sizeBytes / 1024),
                mb: (sizeBytes / 1024 / 1024).toFixed(2)
            };
        } catch (error) {
            return { bytes: 0, kb: 0, mb: '0.00' };
        }
    }

    /**
     * Vérifie si un cache processed peut être généré
     */
    canGenerateCache() {
        return gedcomDataStore.individualsCache &&
               gedcomDataStore.individualsCache.size > 0;
    }

    /**
     * Debug: Log des informations sur le cache actuel
     */
    debugCurrentState() {
        logger.cache.service.group('🔍 État actuel pour génération cache');
        logger.cache.debug('Individus:', gedcomDataStore.individualsCache.size);
        logger.cache.debug('Indices:', !!gedcomDataStore.indices);
        logger.cache.debug('Peut générer cache:', this.canGenerateCache());
        logger.cache.service.groupEnd();
    }
}

// Instance singleton
const processedCacheService = new ProcessedCacheService();
export default processedCacheService;